// Pure logic behind the Clerk Statements screen (Fee Slip / Invoice Payment).
//
// Kept out of the page component so it can be executed in tests rather than asserted
// against source text. These are the parts that can quietly go wrong: grouping that drops
// a statement, a reimbursement tick that overwrites a colleague's, or an unescaped client
// name reaching the printed page.

export const PAYMENT_STATUSES = [
  { value: "not_paid", label: "Outstanding" },
  // Checked and handed to the firm's finance team to pay (Emma, 26 Oct 2026).
  { value: "send_to_finance", label: "Send to Finance for Payment" },
  // Set when the email to finance has been sent from the page (src/lib/financeFeeSlipEmail.js).
  { value: "sent_to_finance", label: "Sent to Finance" },
  { value: "paid", label: "Paid" },
  { value: "partial_paid", label: "Partial Paid" },
  // VLA paid counsel's fee to the firm instead of the barrister, so the firm has to pay it on
  // (Emma, 30 Oct 2026). Flagged on the page and in the morning summary until marked Paid.
  { value: "paid_to_firm", label: "Paid to firm - pay counsel" },
  { value: "retrospective_aid_required", label: "Retrospective Aid Required" },
  { value: "aid_pending", label: "Aid Pending" },
  { value: "aid_not_applied_for", label: "Aid Not Applied For" },
  { value: "to_be_paid_from_budget", label: "To Be Paid From Budget" },
  // Emma, 1 Nov 2026.
  { value: "no_win_no_fee", label: "No Win No Fee" },
  { value: "not_crime_youth", label: "Not a Crime/Youth fee slip" },
  { value: "email_instructor", label: "Email Instructor" },
  { value: "email_clerk", label: "Email Clerk" },
  // Set when the email has been sent from the page (src/lib/clerkEmail.js).
  { value: "instructor_emailed", label: "Instructor Emailed" },
  { value: "clerk_emailed", label: "Clerk Emailed" },
  { value: "other", label: "Other" },
];

export const PAYMENT_STATUS_VALUES = PAYMENT_STATUSES.map((item) => item.value);

// Reserved for statuses that are retired but still present in stored records. Empty today:
// "Partial Paid" was briefly treated as retired, but the clerk statements coming back from
// the Render service use it actively - several invoices are part-paid with the balance
// awaiting further aid - so it is a first-class status above.
const LEGACY_STATUS_LABELS = {};

export const isLegacyPaymentStatus = (status) =>
  Object.prototype.hasOwnProperty.call(LEGACY_STATUS_LABELS, status);

export const normalizePaymentStatus = (status) => {
  if (PAYMENT_STATUS_VALUES.includes(status)) return status;
  if (isLegacyPaymentStatus(status)) return status;
  return "not_paid";
};

export const paymentStatusLabel = (status) => {
  if (isLegacyPaymentStatus(status)) return LEGACY_STATUS_LABELS[status];
  return PAYMENT_STATUSES.find((item) => item.value === normalizePaymentStatus(status))?.label
    || "Outstanding";
};

// Statement text is interpolated into a printable HTML document, so every value has to be
// escaped. Client names and counsel names routinely contain apostrophes and ampersands, and
// an invoice filename comes from whatever the uploader called it.
export const escapePrintHtml = (value) => String(value ?? "")
  .replaceAll("&", "&amp;")
  .replaceAll("<", "&lt;")
  .replaceAll(">", "&gt;")
  .replaceAll('"', "&quot;")
  .replaceAll("'", "&#039;");

// Arranges statements into status groups, in the order the statuses are declared, each
// sorted by client unless another order is given (the page's "Sort by",
// src/lib/feeSlipSort.js). Groups with nothing in them are dropped rather than printed empty.
export function groupStatementsByStatus(
  items = [],
  statusOf = (item) => item.status,
  byClient = (a, b) => String(a.clientName || "").localeCompare(String(b.clientName || "")),
) {
  const groups = PAYMENT_STATUSES.map((option) => ({
    status: option.value,
    label: option.label,
    items: items
      .filter((item) => normalizePaymentStatus(statusOf(item)) === option.value)
      .sort(byClient),
  }));

  // Anything still carrying a retired status gets its own group at the end rather than
  // being folded into Outstanding, so a part-paid invoice is never shown as unpaid.
  const legacy = items.filter((item) => isLegacyPaymentStatus(statusOf(item)));
  for (const status of new Set(legacy.map((item) => statusOf(item)))) {
    groups.push({
      status,
      label: paymentStatusLabel(status),
      items: legacy.filter((item) => statusOf(item) === status).sort(byClient),
    });
  }

  return groups.filter((group) => group.items.length > 0);
}

// Applies one tick or untick to the set of flagged invoice ids.
//
// Returns a new sorted array and never mutates the input. Kept as a delta rather than
// "write the whole list" so that when the server rejects a save because another clerk wrote
// first, the same single change can be re-applied to their newer list instead of replacing
// it wholesale and undoing their work.
export function applyReimbursementDelta(ids = [], invoiceId, shouldReimburse) {
  const next = new Set(ids);
  if (shouldReimburse) next.add(invoiceId);
  else next.delete(invoiceId);
  return [...next].sort();
}

// Every flagged invoice, across all clerks, sorted by clerk then client. Deliberately takes
// the unfiltered list: the Other Statements section has to show the full reimbursement
// position, not whatever the status filters happen to be showing.
export function selectReimbursementStatements(items = [], ids = []) {
  const flagged = new Set(ids);
  return items
    .filter((item) => flagged.has(item.id))
    .sort((a, b) =>
      String(a.clerkName || "").localeCompare(String(b.clerkName || ""))
      || String(a.clientName || "").localeCompare(String(b.clientName || "")));
}

export function sumStatementAmounts(items = []) {
  return items.reduce((sum, item) => sum + (Number.parseFloat(item.invoiceAmount) || 0), 0);
}

// Maps a status as written on a clerk statement to the value used on this screen. The
// statements arrive with human labels ("Retrospective aid required"), and a blank status is
// common - roughly half the rows - meaning nobody has triaged the invoice yet, which is
// Outstanding rather than an error.
export function paymentStatusFromLabel(label) {
  const text = String(label ?? "").trim().toLowerCase();
  if (!text) return "not_paid";
  const match = PAYMENT_STATUSES.find((option) => option.label.toLowerCase() === text);
  return match ? match.value : "other";
}

// Statement amounts are parsed out of clerk PDFs and some come through corrupted - rows in
// the live data carry values like "111769451099.00" where a page footer ran into the amount
// column. Left unchecked, one such row makes a statement total read in the billions, which
// is worse than showing nothing: it looks authoritative and is nonsense.
export const MAX_PLAUSIBLE_INVOICE_AMOUNT = 1000000;

export function parseStatementAmount(value) {
  const amount = Number.parseFloat(String(value ?? "").replace(/[$,\s]/g, ""));
  if (!Number.isFinite(amount) || amount < 0) return { amount: 0, suspect: true };
  if (amount > MAX_PLAUSIBLE_INVOICE_AMOUNT) return { amount: 0, suspect: true };
  return { amount, suspect: false };
}

// Groups statements by clerk/list - Greens, Holmes, Parnell's - so one clerk can be picked
// before drilling into its status groups. Each entry carries its own total, because the
// point of the screen is sending a clerk a copy of what is outstanding with them, and a
// group without a total is not much use for that.
export function groupStatementsByClerk(items = []) {
  const byClerk = new Map();
  for (const item of items) {
    const name = String(item?.clerkName || "").trim() || "Unattributed";
    if (!byClerk.has(name)) byClerk.set(name, []);
    byClerk.get(name).push(item);
  }
  return [...byClerk.entries()]
    .map(([clerk, statements]) => ({
      clerk,
      items: statements,
      count: statements.length,
      total: sumStatementAmounts(statements),
    }))
    .sort((a, b) => a.clerk.localeCompare(b.clerk));
}
